import { motion, useScroll, useTransform } from "framer-motion";
import { Radio } from "lucide-react";
import { IMAGES, waLink, WA_MESSAGES } from "@/lib/site";
import { ArrowButton } from "@/components/landing/Reveal";

const LINES = ["THE BEST NIGHTS.", "RESERVED."];

const Hero = () => {
    const { scrollY } = useScroll();
    const contentY = useTransform(scrollY, [0, 800], [0, 220]);
    const contentOpacity = useTransform(scrollY, [0, 500], [1, 0]);

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative flex min-h-[100svh] items-end overflow-hidden"
        >
            <div className="absolute inset-0">
                <img
                    src={IMAGES.hero}
                    alt="Champagne and glasses on a VIP table in a luxury nightclub"
                    className="hero-zoom h-full w-full object-cover"
                    loading="eager"
                />
                <div className="absolute inset-0 bg-black/55" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-[#050505]/30" />
            </div>

            <motion.div
                style={{ y: contentY, opacity: contentOpacity }}
                className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-28 pt-40 md:px-10 md:pb-36"
            >
                <motion.div
                    data-testid="hero-badge"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9, duration: 0.8 }}
                    className="mb-8 inline-flex items-center gap-3 border border-gold/30 bg-black/40 px-4 py-2 backdrop-blur-sm"
                >
                    <Radio size={13} className="text-gold" strokeWidth={1.5} />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">
                        24/7 VIP Concierge
                    </span>
                </motion.div>

                <h1 className="font-serif text-[13vw] font-medium leading-[0.95] tracking-tight text-ivory sm:text-6xl md:text-7xl xl:text-[6.5rem]">
                    {LINES.map((line, i) => (
                        <span key={line} className="block overflow-hidden pb-1">
                            <motion.span
                                className={`block ${i === 1 ? "italic text-gold" : ""}`}
                                initial={{ y: "115%" }}
                                animate={{ y: 0 }}
                                transition={{
                                    duration: 1.2,
                                    delay: 0.25 + i * 0.18,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                            >
                                {line}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    data-testid="hero-subcopy"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.0, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-8 max-w-xl text-sm leading-relaxed text-ivory/80 md:text-base"
                >
                    VIP tables. Bottle service. Exclusive nightlife experiences.
                    <br />
                    Book your night in Thailand before you even step into the city.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
                >
                    <ArrowButton
                        testId="hero-book-vip-btn"
                        href={waLink(WA_MESSAGES.hero)}
                    >
                        Book Your VIP Table
                    </ArrowButton>
                    <ArrowButton
                        testId="hero-concierge-btn"
                        variant="secondary"
                        href={waLink(WA_MESSAGES.concierge)}
                    >
                        Chat With Our Concierge
                    </ArrowButton>
                </motion.div>

                <motion.p
                    data-testid="hero-trust-line"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.5, duration: 1 }}
                    className="mt-12 text-[11px] font-semibold uppercase tracking-[0.35em] text-ivory/50"
                >
                    Bangkok <span className="mx-2 text-gold">•</span> Phuket
                    <span className="mx-2 text-gold">•</span> Pattaya
                    <span className="mx-2 text-gold">•</span> Koh Samui
                </motion.p>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                className="absolute bottom-8 right-8 z-10 hidden flex-col items-center gap-3 md:flex"
            >
                <span className="text-[9px] font-semibold uppercase tracking-[0.4em] text-ivory/40 [writing-mode:vertical-rl]">
                    Scroll
                </span>
                <span className="h-16 w-px bg-gradient-to-b from-gold/60 to-transparent" />
            </motion.div>
        </section>
    );
};

export default Hero;
